import { useEffect, useMemo, useState } from "react";
import {
  checkInEmployeeAttendance,
  checkOutEmployeeAttendance,
  getEmployeeAttendanceOverview,
  requestEmployeeCompOff,
  requestEmployeeAttendanceRegularization,
} from "../api/instateamAttendanceApi";
import { AppIcon, StatusBanner } from "../components/InstateamEmployeeComponents";
import {
  buildEmployeeAttendanceCalendar,
  createAttendanceDonutStyle,
  downloadAttendanceCsv,
  formatAttendanceHours,
  formatAttendanceStatus,
  formatAttendanceTime,
  formatMonthHeading,
  getAttendanceStatusMeta,
  getCurrentMonthKey,
  shiftMonthKey,
} from "../utils/employeeAttendanceHelpers";

const INITIAL_REGULARIZATION_FORM = {
  dateKey: "",
  requestedStatus: "present",
  requestedCheckInTime: "",
  requestedCheckOutTime: "",
  reason: "",
};

const INITIAL_COMPOFF_FORM = {
  dateKey: "",
  reason: "",
};

function getCsvDate(record) {
  if (record?.dateKey) return `'${record.dateKey}`;
  if (record?.date) {
    const date = new Date(record.date);
    if (!Number.isNaN(date.getTime())) return `'${date.toISOString().slice(0, 10)}`;
  }
  return record?.dateLabel || "";
}

function SummaryCard({ icon, label, value, helper, tone }) {
  return (
    <article className="employee-attendance-metric-card">
      <div className={`employee-attendance-metric-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
    </article>
  );
}

export default function EmployeeAttendancePage({ token, searchText = "" }) {
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegularizationModal, setShowRegularizationModal] = useState(false);
  const [showCompOffModal, setShowCompOffModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [regularizationForm, setRegularizationForm] = useState(INITIAL_REGULARIZATION_FORM);
  const [compOffForm, setCompOffForm] = useState(INITIAL_COMPOFF_FORM);

  useEffect(() => {
    let isActive = true;

    async function fetchOverview() {
      try {
        setIsLoading(true);
        const payload = await getEmployeeAttendanceOverview(token, monthKey);

        if (!isActive) return;

        setOverview(payload.overview || null);
      } catch (error) {
        if (!isActive) return;

        setStatusMessage({
          type: "error",
          text: error.message,
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    fetchOverview();

    return () => {
      isActive = false;
    };
  }, [monthKey, token]);

  const calendar = useMemo(
    () => buildEmployeeAttendanceCalendar(monthKey, overview?.calendar || []),
    [monthKey, overview?.calendar]
  );

  const summary = useMemo(
    () =>
      overview?.summary || {
        present: 0,
        absent: 0,
        late: 0,
        weeklyOff: 0,
        onLeave: 0,
        holiday: 0,
        workingDays: 0,
      },
    [overview?.summary]
  );

  const donutStyle = useMemo(
    () => ({ background: createAttendanceDonutStyle(summary) }),
    [summary]
  );

  const todayRecord = overview?.todayRecord || null;
  const visibleRecentRecords = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    const records = overview?.recentRecords || [];
    if (!query) return records;
    return records.filter((record) =>
      [
        record.dateKey,
        record.dateLabel,
        record.status,
        record.checkInTime,
        record.checkOutTime,
        record.workHoursLabel,
        record.location,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [overview?.recentRecords, searchText]);
  const isOnLeaveToday = todayRecord?.status === "on_leave";
  const canCheckIn = !todayRecord?.checkInAt && !isOnLeaveToday;
  const canCheckOut = Boolean(
    todayRecord?.checkInAt && !todayRecord?.checkOutAt && !isOnLeaveToday
  );

  function openRegularizationModal() {
    const preferredDate =
      todayRecord?.dateKey ||
      overview?.records?.[0]?.dateKey ||
      `${monthKey}-01`;

    setRegularizationForm({
      ...INITIAL_REGULARIZATION_FORM,
      dateKey: preferredDate,
      requestedStatus: todayRecord?.status || "present",
    });
    setShowRegularizationModal(true);
  }

  function openCompOffModal() {
    const preferredDate =
      todayRecord?.dateKey ||
      overview?.records?.find((record) => record.checkInAt || record.workMinutes)?.dateKey ||
      `${monthKey}-01`;

    setCompOffForm({
      ...INITIAL_COMPOFF_FORM,
      dateKey: preferredDate,
    });
    setShowCompOffModal(true);
  }

  async function refreshOverview(nextMonthKey = monthKey) {
    const payload = await getEmployeeAttendanceOverview(token, nextMonthKey);
    setOverview(payload.overview || null);
  }

  async function handleCheckIn() {
    try {
      setIsSubmitting(true);
      setStatusMessage(null);
      await checkInEmployeeAttendance(token, { location: "Office" });
      await refreshOverview();
      setStatusMessage({
        type: "success",
        text: "Checked in successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCheckOut() {
    try {
      setIsSubmitting(true);
      setStatusMessage(null);
      await checkOutEmployeeAttendance(token);
      await refreshOverview();
      setStatusMessage({
        type: "success",
        text: "Checked out successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmitRegularization(event) {
    event.preventDefault();

    if (!regularizationForm.dateKey || !regularizationForm.reason.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Select a date and enter the regularization reason first.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setStatusMessage(null);

      await requestEmployeeAttendanceRegularization(token, regularizationForm);
      setShowRegularizationModal(false);
      await refreshOverview();
      setStatusMessage({
        type: "success",
        text: "Regularization request submitted successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSubmitCompOff(event) {
    event.preventDefault();

    if (!compOffForm.dateKey || !compOffForm.reason.trim()) {
      setStatusMessage({
        type: "warning",
        text: "Select the worked holiday date and enter the reason first.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setStatusMessage(null);
      await requestEmployeeCompOff(token, compOffForm);
      setShowCompOffModal(false);
      await refreshOverview();
      setStatusMessage({
        type: "success",
        text: "Comp off request submitted successfully.",
      });
    } catch (error) {
      setStatusMessage({
        type: "error",
        text: error.message,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleDownloadReport() {
    const rows = [
      ["Date", "Status", "Check In", "Check Out", "Work Hours", "Location"],
      ...(overview?.records || []).map((record) => [
        getCsvDate(record),
        formatAttendanceStatus(record.status),
        record.checkInTime || "-",
        record.checkOutTime || "-",
        record.workHoursLabel || "-",
        record.location || "Office",
      ]),
    ];

    downloadAttendanceCsv(`instateam-attendance-${monthKey}.csv`, rows);
  }

  return (
    <section className="employee-portal-page">
      <div className="employee-page-heading employee-attendance-heading">
        <div>
          <h1>Attendance</h1>
          <div className="employee-breadcrumb">
            <span>Dashboard</span>
            <span>></span>
            <span>Attendance</span>
          </div>
        </div>

        <div className="employee-attendance-page-actions">
          <button
            className="secondary-button employee-inline-button"
            type="button"
            onClick={openCompOffModal}
          >
            Comp Off
          </button>
          <button
            className="secondary-button employee-inline-button"
            type="button"
            onClick={openRegularizationModal}
          >
            Regularization Request
          </button>
          <button
            className="primary-button employee-inline-button"
            type="button"
            onClick={handleDownloadReport}
          >
            <AppIcon name="download" />
            Attendance Report
          </button>
        </div>
      </div>

      {statusMessage ? (
        <StatusBanner
          type={statusMessage.type}
          text={statusMessage.text}
          onClose={() => setStatusMessage(null)}
        />
      ) : null}

      <div className="employee-attendance-metrics-grid">
        <SummaryCard
          icon="calendarCheck"
          label="Present Days"
          value={summary.present}
          helper="This month"
          tone="success"
        />
        <SummaryCard
          icon="calendarX"
          label="Absent Days"
          value={summary.absent}
          helper="This month"
          tone="danger"
        />
        <SummaryCard
          icon="clock"
          label="Late Days"
          value={summary.late}
          helper="This month"
          tone="info"
        />
        <SummaryCard
          icon="calendar"
          label="Total Working Days"
          value={summary.workingDays}
          helper="This month"
          tone="warning"
        />
      </div>

      <div className="employee-attendance-layout">
        <div className="employee-attendance-main">
          <article className="employee-attendance-card">
            <div className="employee-attendance-card-header">
              <h3>Attendance Calendar</h3>

              <div className="employee-attendance-calendar-tools">
                <button
                  type="button"
                  className="employee-calendar-arrow"
                  onClick={() => setMonthKey((current) => shiftMonthKey(current, -1))}
                >
                  <AppIcon name="chevronLeft" />
                </button>
                <strong>{formatMonthHeading(monthKey)}</strong>
                <button
                  type="button"
                  className="employee-calendar-arrow"
                  onClick={() => setMonthKey((current) => shiftMonthKey(current, 1))}
                >
                  <AppIcon name="chevronRight" />
                </button>
              </div>
            </div>

            {isLoading ? (
              <div className="employee-attendance-loading">Loading attendance...</div>
            ) : (
              <div className="employee-attendance-calendar-shell">
                <div className="employee-attendance-legend">
                  {["present", "absent", "late", "weekly_off"].map((status) => {
                    const meta = getAttendanceStatusMeta(status);

                    return (
                      <span key={status} className="employee-attendance-legend-item">
                        <em className={`attendance-dot ${meta.tone}`} />
                        {meta.label}
                      </span>
                    );
                  })}
                </div>

                <div className="employee-attendance-grid">
                  {calendar.weekdays.map((weekday) => (
                    <div key={weekday} className="employee-attendance-weekday">
                      {weekday}
                    </div>
                  ))}

                  {calendar.cells.map((cell) =>
                    cell.isPlaceholder ? (
                      <div key={cell.key} className="employee-attendance-cell placeholder" />
                    ) : (
                      <div key={cell.key} className="employee-attendance-cell">
                        <span className="employee-attendance-day">{cell.day}</span>
                        {cell.status ? (
                          <span className={`employee-attendance-pill ${cell.meta.tone}`}>
                            {cell.meta.shortLabel}
                          </span>
                        ) : null}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </article>

          <article className="employee-attendance-card">
            <div className="employee-attendance-card-header">
              <h3>Recent Attendance Records</h3>
              <button
                type="button"
                className="employee-link-button"
                onClick={handleDownloadReport}
              >
                Download CSV
              </button>
            </div>

            <div className="employee-attendance-table-shell">
              <div className="employee-attendance-table-header">
                <span>Date</span>
                <span>Status</span>
                <span>Check In</span>
                <span>Check Out</span>
                <span>Work Hours</span>
                <span>Location</span>
              </div>

              {visibleRecentRecords.length ? (
                visibleRecentRecords.map((record) => {
                  const meta = getAttendanceStatusMeta(record.status);

                  return (
                    <div key={record._id} className="employee-attendance-table-row">
                      <span>{record.dateLabel}</span>
                      <span className={`employee-status-chip ${meta.tone}`}>
                        {meta.label}
                      </span>
                      <span>{formatAttendanceTime(record.checkInTime)}</span>
                      <span>{formatAttendanceTime(record.checkOutTime)}</span>
                      <span>{formatAttendanceHours(record.workHoursLabel)}</span>
                      <span>{record.location || "Office"}</span>
                    </div>
                  );
                })
              ) : (
                <div className="employee-attendance-empty">
                  No attendance records found for this month.
                </div>
              )}
            </div>
          </article>
        </div>

        <div className="employee-attendance-side">
          <article className="employee-attendance-card employee-today-card">
            <div className="employee-attendance-card-header">
              <h3>Today&apos;s Attendance</h3>
              <span
                className={`employee-status-chip ${
                  getAttendanceStatusMeta(todayRecord?.status || "present").tone
                }`}
              >
                {todayRecord?.status
                  ? formatAttendanceStatus(todayRecord.status)
                  : "Pending"}
              </span>
            </div>

            <div className="employee-today-card-body">
              <div className="employee-today-icon">
                <AppIcon
                  name={
                    isOnLeaveToday
                      ? "plane"
                      : todayRecord?.checkOutAt
                      ? "checkCircle"
                      : todayRecord?.checkInAt
                        ? "clock"
                        : "calendarCheck"
                  }
                />
              </div>

              <h4>
                {isOnLeaveToday
                  ? "On approved leave today"
                  : todayRecord?.checkInTime
                  ? `Checked In at ${todayRecord.checkInTime}`
                  : "Not checked in yet"}
              </h4>
              <p>
                {todayRecord?.dateLabel || "Start your workday by marking attendance."}
              </p>

              <div className="employee-today-times">
                <div>
                  <span>Check In</span>
                  <strong>{formatAttendanceTime(todayRecord?.checkInTime)}</strong>
                </div>
                <div>
                  <span>Check Out</span>
                  <strong>{formatAttendanceTime(todayRecord?.checkOutTime)}</strong>
                </div>
              </div>

              {canCheckIn ? (
                <button
                  type="button"
                  className="primary-button employee-action-button"
                  onClick={handleCheckIn}
                  disabled={isSubmitting}
                >
                  <AppIcon name="logIn" />
                  {isSubmitting ? "Processing..." : "Check In"}
                </button>
              ) : canCheckOut ? (
                <button
                  type="button"
                  className="secondary-button employee-action-button checkout"
                  onClick={handleCheckOut}
                  disabled={isSubmitting}
                >
                  <AppIcon name="logOut" />
                  {isSubmitting ? "Processing..." : "Check Out"}
                </button>
              ) : isOnLeaveToday ? (
                <button
                  type="button"
                  className="secondary-button employee-action-button"
                  disabled
                >
                  Approved Leave
                </button>
              ) : (
                <button
                  type="button"
                  className="secondary-button employee-action-button"
                  disabled
                >
                  Attendance Completed
                </button>
              )}
            </div>
          </article>

          <article className="employee-attendance-card">
            <div className="employee-attendance-card-header">
              <h3>Attendance Summary</h3>
              <span>{formatMonthHeading(monthKey)}</span>
            </div>

            <div className="employee-summary-chart">
              <div className="employee-summary-donut" style={donutStyle}>
                <div className="employee-summary-donut-center">
                  <strong>{summary.workingDays}</strong>
                  <span>Total Days</span>
                </div>
              </div>

              <div className="employee-summary-list">
                {[
                  ["Present", summary.present, "success"],
                  ["Absent", summary.absent, "danger"],
                  ["Late", summary.late, "warning"],
                  ["On Leave", summary.onLeave, "info"],
                  ["Weekly Off", summary.weeklyOff, "muted"],
                ].map(([label, value, tone]) => (
                  <div key={label} className="employee-summary-list-item">
                    <span>
                      <em className={`attendance-dot ${tone}`} />
                      {label}
                    </span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </div>

      {showRegularizationModal ? (
        <div className="employee-modal-overlay" role="dialog" aria-modal="true">
          <div className="employee-regularization-modal">
            <div className="employee-attendance-card-header">
              <div>
                <h3>Regularization Request</h3>
                <p className="employee-modal-copy">
                  Submit a correction request for an attendance date.
                </p>
              </div>
              <button
                type="button"
                className="employee-portal-icon-button"
                onClick={() => setShowRegularizationModal(false)}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <form className="employee-regularization-form" onSubmit={handleSubmitRegularization}>
              <label className="form-field">
                <span>Date</span>
                <div className="field-shell">
                  <input
                    type="date"
                    value={regularizationForm.dateKey}
                    onChange={(event) =>
                      setRegularizationForm((current) => ({
                        ...current,
                        dateKey: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>

              <label className="form-field">
                <span>Requested Status</span>
                <div className="field-shell select-shell">
                  <select
                    value={regularizationForm.requestedStatus}
                    onChange={(event) =>
                      setRegularizationForm((current) => ({
                        ...current,
                        requestedStatus: event.target.value,
                      }))
                    }
                  >
                    <option value="present">Present</option>
                    <option value="late">Late</option>
                    <option value="absent">Absent</option>
                    <option value="on_leave">On Leave</option>
                  </select>
                  <AppIcon name="chevronDown" />
                </div>
              </label>

              <div className="employee-regularization-time-grid">
                <label className="form-field">
                  <span>Requested Check In</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={regularizationForm.requestedCheckInTime}
                      onChange={(event) =>
                        setRegularizationForm((current) => ({
                          ...current,
                          requestedCheckInTime: event.target.value,
                        }))
                      }
                    />
                  </div>
                </label>

                <label className="form-field">
                  <span>Requested Check Out</span>
                  <div className="field-shell">
                    <input
                      type="time"
                      value={regularizationForm.requestedCheckOutTime}
                      onChange={(event) =>
                        setRegularizationForm((current) => ({
                          ...current,
                          requestedCheckOutTime: event.target.value,
                        }))
                      }
                    />
                  </div>
                </label>
              </div>

              <label className="form-field">
                <span>Reason</span>
                <div className="employee-textarea-shell">
                  <textarea
                    rows={5}
                    value={regularizationForm.reason}
                    onChange={(event) =>
                      setRegularizationForm((current) => ({
                        ...current,
                        reason: event.target.value,
                      }))
                    }
                    placeholder="Explain why this attendance needs regularization."
                  />
                </div>
              </label>

              <div className="employee-regularization-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowRegularizationModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button">
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showCompOffModal ? (
        <div className="employee-modal-overlay" role="dialog" aria-modal="true">
          <div className="employee-regularization-modal employee-compact-request-modal">
            <div className="employee-attendance-card-header">
              <div>
                <h3>Comp Off Request</h3>
                <p className="employee-modal-copy">
                  Select the holiday or weekly-off day you worked and share the reason.
                </p>
              </div>
              <button
                type="button"
                className="employee-portal-icon-button"
                onClick={() => setShowCompOffModal(false)}
              >
                <AppIcon name="close" />
              </button>
            </div>

            <form className="employee-regularization-form compact" onSubmit={handleSubmitCompOff}>
              <label className="form-field">
                <span>Worked Holiday Date</span>
                <div className="field-shell">
                  <input
                    type="date"
                    value={compOffForm.dateKey}
                    onChange={(event) =>
                      setCompOffForm((current) => ({
                        ...current,
                        dateKey: event.target.value,
                      }))
                    }
                  />
                </div>
              </label>

              <label className="form-field">
                <span>Reason of Working</span>
                <div className="employee-textarea-shell compact">
                  <textarea
                    rows={4}
                    value={compOffForm.reason}
                    onChange={(event) =>
                      setCompOffForm((current) => ({
                        ...current,
                        reason: event.target.value,
                      }))
                    }
                    placeholder="Explain why you worked on this holiday or weekly off."
                  />
                </div>
              </label>

              <div className="employee-regularization-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowCompOffModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button">
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </section>
  );
}
